import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fileToBase64, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  CheckCircle2, MapPin, Calendar, Download, Upload, ArrowRight,
  Sparkles, FileText, Bell, MessageSquare, CreditCard, ChevronRight,
  Eye, RotateCcw, Lock, Check, ExternalLink,
  Users, UserPlus, Trash2, X,
} from "lucide-react";
import { DASH } from "@/constants/testIds";

/**
 * Prime Supplier Dashboard — Phase 1 MVP.
 * Delta-app-after-booking / Apple-Wallet aesthetic. Cards over tables.
 * Answers one question: "What do I need to do next?"
 */
const EVENT_DATE_ISO = "2026-09-11T09:00:00-04:00"; // Fallback if /event is unavailable

// Display list of 11 stages the supplier sees. Backend stores 8; two are virtual
// (station reserved) and two are placeholders (Packed / Installed) — advanced
// admins can move suppliers through them once we split the production_complete step.
const DISPLAY_STAGES = [
  { key: "registered",           label: "Registration Complete" },
  { key: "station_reserved",     label: "Supplier Station Reserved" }, // virtual
  { key: "payment_received",     label: "Payment Complete" },
  { key: "logo_received",        label: "Logo Received" },
  { key: "graphics_in_design",   label: "Artwork In Design" },
  { key: "proof_ready",          label: "Proof Ready" },
  { key: "approved",             label: "Approved" },
  { key: "production_complete",  label: "In Production" },
  { key: "packed",               label: "Packed" },           // future
  { key: "installed",            label: "Installed" },        // future
  { key: "ready_for_event",      label: "Ready for Event" },
];

function SurveyBanner() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/survey/active").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data || !data.survey) return null;
  if (data.submitted) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3 text-sm" data-testid="dash-survey-thanks">
        <CheckCircle2 size={18} className="text-emerald-600" />
        <span>Thank you — your Summit feedback has been submitted.</span>
      </div>
    );
  }
  return (
    <div className="border border-black bg-black text-white p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="dash-survey-card">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-white/60">Post-Event Survey</div>
        <div className="serif text-xl mt-1">How was your Summit experience?</div>
        <div className="text-sm text-white/70 mt-1">Takes less than two minutes — your feedback shapes future events.</div>
      </div>
      <Link to="/survey" className="bg-white text-black px-6 h-11 inline-flex items-center justify-center text-xs uppercase tracking-[0.14em] whitespace-nowrap" data-testid="dash-survey-cta">
        Share Your Feedback
      </Link>
    </div>
  );
}


export default function ExhibitorDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [exhibitor, setExhibitor] = useState(null);
  const [production, setProduction] = useState(null);
  const [station, setStation] = useState(null);
  const [event, setEvent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [itinerary, setItinerary] = useState({ role: null, rounds: [], appointments: [] });
  const [documents, setDocuments] = useState([]);
  const [eventDocs, setEventDocs] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [proof, setProof] = useState(null);
  const [loadingE, setLoadingE] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    Promise.allSettled([
      api.get("/exhibitors/me").then(({ data }) => setExhibitor(data.exhibitor)),
      api.get("/exhibitors/me/production").then(({ data }) => setProduction(data)),
      api.get("/exhibitors/me/station").then(({ data }) => setStation(data.station)).catch(() => {}),
      api.get("/exhibitors/me/appointments").then(({ data }) => setAppointments(data.appointments || [])).catch(() => {}),
      api.get("/matchmaking/me/itinerary").then(({ data }) => setItinerary(data)).catch(() => {}),
      api.get("/event").then(({ data }) => setEvent(data)),
      api.get("/sessions").then(({ data }) => setSessions(data.sessions || [])),
      api.get("/event-documents").then(({ data }) => setDocuments(data.documents || [])).catch(() => {}),
      api.get("/event-docs").then(({ data }) => setEventDocs(data.documents || [])).catch(() => {}),
      api.get("/executive-updates").then(({ data }) => setUpdates(data.updates || [])).catch(() => {}),
      api.get("/exhibitors/me/proof").then(({ data }) => setProof(data.proof)).catch(() => {}),
    ]).finally(() => setLoadingE(false));
  }, [user, loading, nav]);

  if (loading || loadingE) {
    return <Layout><section className="container-summit py-24"><div className="overline">Loading your dashboard…</div></section></Layout>;
  }
  if (!exhibitor) {
    // Attendees (SBE / T+L / Summit Guest / Speaker) sometimes land here from
    // email links — route them to where their schedule actually lives instead
    // of showing a Prime-registration error.
    if (user?.role === "attendee") {
      const hasSchedule = itinerary?.role === "sbe" || itinerary?.role === "tl"
        || (itinerary?.appointments?.length > 0) || (itinerary?.tl_meetings?.length > 0);
      return (
        <Layout>
          <section className="container-summit py-24 max-w-xl text-center" data-testid="dash-attendee-redirect">
            <div className="overline justify-center"><span className="accent-rule" />Summit Attendee</div>
            <h1 className="text-3xl mt-4">You're signed in as a Summit attendee.</h1>
            <p className="mt-4 text-zinc-500">
              {hasSchedule
                ? "Your Business Matchmaking schedule — meeting times, companies, and table assignments — lives on the Matchmaking page."
                : "This page is the Prime Supplier dashboard. Your Summit resources are just one tap away."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/matchmaking" className="btn-black inline-flex" data-testid="dash-attendee-mm-link">
                View My Matchmaking Schedule <ChevronRight size={16} />
              </Link>
              <Link to="/documents" className="btn-outline-black inline-flex" data-testid="dash-attendee-docs-link">
                Event Documents
              </Link>
            </div>
            <div className="mt-8 text-left"><SurveyBanner /></div>
          </section>
        </Layout>
      );
    }
    return (
      <Layout>
        <section className="container-summit py-24 max-w-xl text-center">
          <h1 className="text-3xl">No supplier profile yet.</h1>
          <p className="mt-4 text-zinc-500">Complete your Prime Supplier registration to access your dashboard.</p>
          <Link to="/onboarding" className="btn-black mt-8 inline-flex">Continue Registration <ChevronRight size={16} /></Link>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Welcome exhibitor={exhibitor} event={event} />
      <section className="container-summit pt-6" data-testid="dash-postevent-message">
        <div className="border border-[#A39171]/40 bg-[#FBF9F5] px-6 py-4 text-sm text-zinc-700">
          Thank you for participating in the inaugural Travel + Leisure Strategic Supplier Summit.
        </div>
      </section>
      <section className="container-summit pt-6"><SurveyBanner /></section>

      {exhibitor._viewer_is_primary === false && (
        <section className="bg-[#A39171]/10 border-y border-[#A39171]/40">
          <div className="container-summit py-3 text-xs uppercase tracking-[0.18em] text-[#A39171]"
               data-testid="dash-viewer-additional-banner">
            Viewing as Additional Representative · View-only access
          </div>
        </section>
      )}

      <section className="container-summit pb-16">
        <div className="grid lg:grid-cols-[2fr_1fr] gap-6 mt-8">
          {/* LEFT column — priority actions & progress */}
          <div className="grid gap-6">
            <ActionCenter exhibitor={exhibitor} production={production} setExhibitor={setExhibitor} station={station} />
            {exhibitor._viewer_is_primary !== false && <PaymentStatusCard exhibitor={exhibitor} />}
            {exhibitor._viewer_is_primary !== false && <ProofReviewCard proof={proof} setProof={setProof} setProduction={setProduction} />}
            <ProgressCard production={production} station={station} />
            <ScheduleCard sessions={sessions} />
            <MatchmakingCard appointments={appointments} itinerary={itinerary} />
          </div>

          {/* RIGHT column — station, docs, concierge, notifications */}
          <aside className="grid gap-6 content-start">
            <StationCard station={station} />
            <RepresentativesCard exhibitor={exhibitor} setExhibitor={setExhibitor} />
            <ConciergeCard />
            <DocumentCenter exhibitor={exhibitor} documents={documents} eventDocs={eventDocs} />
            <NotificationsCard updates={updates} exhibitor={exhibitor} production={production} proof={proof} />
          </aside>
        </div>
      </section>
    </Layout>
  );
}

/* -------- 1) Welcome + Countdown -------- */
function Welcome({ exhibitor, event }) {
  const eventStart = event?.event_start_iso || EVENT_DATE_ISO;
  const days = useMemo(() => {
    try {
      const t = new Date(eventStart).getTime() - Date.now();
      return Math.max(0, Math.floor(t / 86400000));
    } catch { return null; }
  }, [eventStart]);

  return (
    <section className="bg-[#0A0A0A] text-white">
      <div className="container-summit py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="overline text-white/60"><span className="accent-rule" />Supplier Dashboard</div>
          <h1 className="serif text-4xl sm:text-6xl mt-4 leading-tight" data-testid="dash-welcome">
            Welcome, {exhibitor.company_name || "Supplier"}.
          </h1>
          <div className="mt-6 flex flex-wrap items-baseline gap-x-8 gap-y-2 text-white/75">
            <span className="text-sm">{event?.name || "Strategic Supplier Summit 2026"}</span>
            <span className="text-sm text-white/50">·</span>
            <span className="text-sm">{event?.date || "Friday, September 11, 2026"}</span>
          </div>

          {days != null && (
            <div className="mt-8 inline-flex items-baseline gap-3 border-l-2 border-[#A39171] pl-5" data-testid="dash-countdown">
              <span className="serif text-5xl sm:text-6xl tabular-nums">{days}</span>
              <span className="text-xs uppercase tracking-[0.24em] text-white/70">
                {days === 1 ? "Day" : "Days"} Remaining
              </span>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

/* -------- 2) Action Center (outstanding tasks only) -------- */
function ActionCenter({ exhibitor, production, setExhibitor, station }) {
  const ps = exhibitor.payment_status || "";
  const paid = ["payment_received", "complete", "paid"].includes(ps);
  const hasStation = !!station;
  const hasLogo = !!exhibitor.logo_file_id;
  const isPrimary = exhibitor._viewer_is_primary !== false;

  // Additional Representatives never see the primary action ladder —
  // station selection, invoicing, logo upload, and proof approval are
  // Primary Rep responsibilities. Skip the whole ladder for them.
  if (!isPrimary) return null;

  const actions = [];
  if (!hasStation) {
    actions.push({
      key: "reserve_station",
      title: "Reserve your Supplier Station",
      body: "Your assigned station on the Supplier Showcase floor.",
      cta: "Continue Registration", to: "/onboarding",
      icon: MapPin,
    });
  }
  if (hasStation && !paid) {
    // QuickBooks manual-invoicing flow — informational only. Display America
    // will email the invoice; the supplier cannot self-pay from the dashboard.
    const inv = exhibitor.invoice_number ? `Invoice ${exhibitor.invoice_number}` : null;
    const invBody = ps === "invoice_sent"
      ? `Your invoice ${inv ? `(${exhibitor.invoice_number}) ` : ""}has been emailed to ${exhibitor.email}. Once payment is received, your station will be permanently confirmed and your dashboard will automatically update.`
      : "Display America will email your invoice shortly. Once payment is received, your registration will be confirmed and your dashboard will automatically update.";
    actions.push({
      key: "awaiting_invoice",
      title: ps === "invoice_sent" ? "Invoice sent — awaiting payment" : "Awaiting invoice from Display America",
      body: invBody,
      cta: exhibitor.invoice_url ? "Open Invoice" : null,
      href: exhibitor.invoice_url || null,
      icon: FileText,
      informational: true,
    });
  }
  if (!hasLogo) {
    actions.push({
      key: "upload_logo",
      title: "Upload your logo",
      body: "Travel + Leisure will design your entire Supplier Station around it. Upload as soon as possible so production stays on schedule.",
      cta: "Upload Logo",
      icon: Upload,
      inlineUpload: true,
    });
  }
  // Once past proof_ready, prompt to approve
  const currentIdx = production?.stages?.findIndex((s) => s.current) ?? -1;
  const rawStage = production?.stages?.[currentIdx]?.key;
  if (rawStage === "proof_ready") {
    actions.push({
      key: "review_proof",
      title: "Review your production proof",
      body: "Approve your station artwork before we send it to production.",
      cta: "Contact Display America",
      href: "mailto:info@displayamerica.com?subject=Proof%20review%20-%20" + encodeURIComponent(exhibitor.company_name),
      icon: FileText,
    });
  }

  if (actions.length === 0) {
    return (
      <Card testId="dash-action-center-empty">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-200">
            <CheckCircle2 size={20} strokeWidth={1.4} className="text-emerald-700" />
          </div>
          <div>
            <div className="serif text-2xl">You&apos;re all set.</div>
            <p className="mt-2 text-sm text-zinc-600">We&apos;ll notify you when something requires your attention.</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card testId="dash-action-center">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">Action Center</div>
          <div className="serif text-2xl mt-1">Next steps</div>
        </div>
        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 tabular-nums">
          {actions.length} outstanding
        </span>
      </div>
      <ul className="divide-y divide-zinc-100">
        {actions.map((a, i) => (
          <ActionRow key={a.key} action={a} exhibitor={exhibitor} setExhibitor={setExhibitor} last={i === actions.length - 1} />
        ))}
      </ul>
    </Card>
  );
}

function ActionRow({ action, exhibitor, setExhibitor }) {
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const base64 = await fileToBase64(file);
      const { data: up } = await api.post("/uploads", {
        filename: file.name, content_type: file.type || "application/octet-stream",
        data_base64: base64, purpose: "logo",
      });
      const { data } = await api.put("/exhibitors/me", { logo_file_id: up.file_id });
      setExhibitor(data.exhibitor);
      toast.success("Logo uploaded. Thank you.");
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  return (
    <li className="py-4 flex items-start gap-4" data-testid={`dash-action-${action.key}`}>
      <div className="h-9 w-9 rounded-full bg-zinc-100 flex items-center justify-center shrink-0">
        <action.icon size={16} strokeWidth={1.4} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{action.title}</div>
        <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{action.body}</div>
      </div>
      {action.inlineUpload ? (
        <label className="btn-outline-black h-9 px-4 text-xs whitespace-nowrap cursor-pointer" data-testid={DASH.uploadLogoBtn}>
          <Upload size={12} /> {busy ? "…" : action.cta}
          <input type="file" hidden accept="image/*,application/pdf,application/postscript,.eps,.ai" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      ) : action.onClick ? (
        <button onClick={action.onClick}
          data-testid={DASH.payNowBtn}
          className="btn-black h-9 px-4 text-xs whitespace-nowrap">
          {action.cta}
        </button>
      ) : action.to ? (
        <Link to={action.to} className="btn-black h-9 px-4 text-xs whitespace-nowrap">
          {action.cta} <ArrowRight size={12} />
        </Link>
      ) : action.href ? (
        <a href={action.href} className="btn-outline-black h-9 px-4 text-xs whitespace-nowrap">
          {action.cta}
        </a>
      ) : null}
    </li>
  );
}

/* -------- 2b) Payment Status Card (QuickBooks manual invoicing) -------- */
const PAYMENT_STEPS = [
  { key: "awaiting_invoice",  label: "Awaiting Invoice",     hint: "Display America will email your invoice shortly." },
  { key: "invoice_sent",      label: "Invoice Sent",         hint: "Your invoice from Display America has been emailed to you." },
  { key: "awaiting_payment",  label: "Awaiting Payment",     hint: "Waiting for payment through the link in your invoice email." },
  { key: "payment_received",  label: "Payment Received",     hint: "Payment received. Finalizing your registration." },
  { key: "complete",          label: "Registration Complete", hint: "You're fully confirmed. Your station is permanently assigned." },
];

/** Derive the current visual step index (0..4) from the exhibitor record. */
function currentPaymentStepIdx(exhibitor) {
  const s = exhibitor?.payment_status || "";
  if (s === "cancelled") return -1;
  // Complete = payment_received + confirmation email delivered
  if (s === "complete") return 4;
  if (["payment_received", "paid"].includes(s)) {
    return exhibitor?.confirmation_email_sent_at ? 4 : 3;
  }
  // invoice_sent maps to both "Invoice Sent" (2) and "Awaiting Payment" (2). We
  // show Awaiting Payment as the current step since it best describes the
  // supplier's action item.
  if (s === "invoice_sent") return 2;
  return 0; // awaiting_invoice (or legacy unpaid/pending)
}

function PaymentStatusCard({ exhibitor }) {
  const idx = currentPaymentStepIdx(exhibitor);
  const cancelled = exhibitor?.payment_status === "cancelled";
  const currentStep = idx >= 0 ? PAYMENT_STEPS[idx] : null;
  const amt = exhibitor?.invoice_amount || 2500;

  return (
    <div className="bg-white border border-black" data-testid="payment-status-card">
      <div className="p-6 md:p-7 border-b border-zinc-200 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Payment Status</div>
          <div className="serif text-2xl mt-1">
            {cancelled ? "Registration Cancelled" : currentStep?.label || "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="serif text-3xl tabular-nums">${amt.toLocaleString()}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">Supplier Package</div>
        </div>
      </div>

      {cancelled ? (
        <div className="p-6 text-sm text-zinc-600" data-testid="payment-cancelled-note">
          This registration has been cancelled. To reinstate, please email
          <a href="mailto:info@displayamerica.com" className="text-black underline decoration-[#A39171] underline-offset-4"> info@displayamerica.com</a>.
        </div>
      ) : (
        <>
          {/* Horizontal step tracker */}
          <div className="p-6 md:p-7" data-testid="payment-status-tracker">
            <div className="grid grid-cols-5 gap-2">
              {PAYMENT_STEPS.map((s, i) => {
                const done = i < idx;
                const active = i === idx;
                return (
                  <div key={s.key} className="flex flex-col items-center text-center gap-2" data-testid={`ps-step-${i}`}>
                    <div className="relative w-full h-1">
                      <div className={`absolute inset-0 ${done || active ? "bg-[#A39171]" : "bg-zinc-200"}`} />
                    </div>
                    <div className={`inline-flex items-center justify-center w-6 h-6 border rounded-full text-[10px] tabular-nums ${
                      done ? "bg-[#A39171] text-white border-[#A39171]"
                      : active ? "bg-black text-white border-black"
                      : "bg-white text-zinc-400 border-zinc-300"
                    }`}>
                      {done ? <Check size={11} strokeWidth={3} /> : i + 1}
                    </div>
                    <div className={`text-[9px] uppercase tracking-[0.12em] leading-tight ${active ? "text-black font-medium" : done ? "text-zinc-700" : "text-zinc-400"}`}>
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="px-6 md:px-7 pb-6 md:pb-7">
            <div className="p-4 bg-zinc-50 border border-zinc-200 text-sm text-zinc-700 leading-relaxed" data-testid="payment-status-hint">
              {currentStep?.hint}
            </div>

            {/* Invoice details visible once admin has captured them */}
            {(exhibitor.invoice_number || exhibitor.invoice_url) && (
              <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {exhibitor.invoice_number && (
                  <MetaRow label="Invoice Number" value={exhibitor.invoice_number} />
                )}
                {exhibitor.invoice_date && (
                  <MetaRow label="Invoice Date" value={new Date(exhibitor.invoice_date).toLocaleDateString()} />
                )}
                {exhibitor.invoice_sent_at && (
                  <MetaRow label="Invoice Sent" value={new Date(exhibitor.invoice_sent_at).toLocaleDateString()} />
                )}
                {exhibitor.payment_received_at && (
                  <MetaRow label="Payment Received" value={new Date(exhibitor.payment_received_at).toLocaleDateString()} />
                )}
              </div>
            )}

            {exhibitor.invoice_url && idx <= 2 && (
              <a href={exhibitor.invoice_url} target="_blank" rel="noreferrer"
                 className="mt-5 btn-black h-11 px-5 text-xs inline-flex" data-testid="ps-open-invoice">
                Open Invoice <ExternalLink size={12} />
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-zinc-100 py-1.5 last:border-0">
      <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</span>
      <span className="text-zinc-800 tabular-nums">{value}</span>
    </div>
  );
}


/* -------- 3) Registration Status / Progress Card -------- */
function ProgressCard({ production, station }) {
  // Backend gives 8 stages; we render 11 with two virtual (station_reserved / packed / installed)
  const bStages = production?.stages || [];
  const bMap = new Map(bStages.map((s) => [s.key, s]));
  const stationReservedComplete = !!station; // held OR approved counts

  const rows = DISPLAY_STAGES.map((s) => {
    if (s.key === "station_reserved") {
      return { ...s, complete: stationReservedComplete };
    }
    if (s.key === "packed" || s.key === "installed") {
      const rfe = bMap.get("ready_for_event");
      return { ...s, complete: !!rfe?.complete };
    }
    const b = bMap.get(s.key);
    return { ...s, complete: !!b?.complete };
  });

  // Only the earliest incomplete row is "current" — a clean single frontier.
  const firstCurrentIdx = rows.findIndex((r) => !r.complete);
  const normalizedRows = rows.map((r, i) => ({ ...r, current: i === firstCurrentIdx }));

  // Find the highest-index current or first-incomplete
  const currentIdx = normalizedRows.findIndex((r) => r.current);

  return (
    <Card testId="dash-progress">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="overline">Registration Status</div>
          <div className="serif text-2xl mt-1">Your production timeline</div>
        </div>
        {currentIdx >= 0 && normalizedRows[currentIdx] && (
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#A39171]">
            Currently · {normalizedRows[currentIdx].label}
          </span>
        )}
      </div>
      <ol className="grid gap-0">
        {normalizedRows.map((r, i) => (
          <li key={r.key} className="flex items-start gap-3 py-2" data-testid={`dash-stage-${r.key}`}>
            <StageDot complete={r.complete} current={r.current} isLast={i === normalizedRows.length - 1} />
            <div className="flex-1">
              <div className={`text-sm ${r.current ? "font-medium text-black" : r.complete ? "text-zinc-700" : "text-zinc-400"}`}>
                {r.label}
              </div>
            </div>
            {r.complete && <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-1" />}
            {r.current && <span className="text-[10px] uppercase tracking-[0.22em] text-[#A39171] mt-1">In Progress</span>}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function StageDot({ complete, current, isLast }) {
  return (
    <div className="flex flex-col items-center shrink-0">
      <div className={`h-3 w-3 rounded-full border ${
        current ? "bg-[#A39171] border-[#A39171] ring-4 ring-[#A39171]/15"
        : complete ? "bg-black border-black"
        : "bg-white border-zinc-300"
      }`} />
      {!isLast && <div className={`w-px flex-1 mt-1 ${complete ? "bg-black" : "bg-zinc-200"}`} style={{ minHeight: 18 }} />}
    </div>
  );
}

/* -------- 4) Your Supplier Station -------- */
function StationCard({ station }) {
  if (!station) {
    return (
      <Card testId="dash-station-empty">
        <div className="overline">Your Supplier Station</div>
        <div className="serif text-2xl mt-2 text-zinc-400">Not yet reserved</div>
        <p className="text-sm text-zinc-500 mt-3">Your assigned station on the Supplier Showcase floor.</p>
        <Link to="/onboarding" className="btn-black mt-5 w-full h-10 text-xs justify-center">
          Reserve Now <ArrowRight size={12} />
        </Link>
      </Card>
    );
  }

  const isPending = station.status === "reserved" || station.status === "held";
  const isApproved = station.status === "approved";
  const badgeLabel = isApproved ? "Assigned" : isPending ? "Reserved" : (station.status || "").toUpperCase();
  const badgeTone = isApproved ? "text-emerald-700" : isPending ? "text-amber-700" : "text-zinc-600";

  return (
    <Card testId="dash-station">
      <div className="overline">Your Supplier Station</div>
      <div className="flex items-center gap-4 mt-3">
        <div className="serif text-6xl tabular-nums leading-none">{station.label}</div>
        <div className={`text-[10px] uppercase tracking-[0.22em] ${badgeTone}`}>
          {badgeLabel}
        </div>
      </div>
      <StationThumbnail number={station.number} />
      <Link to={`/venue?station=${station.number}`}
        className="btn-outline-black mt-5 w-full h-10 text-xs justify-center"
        data-testid="dash-station-view-floorplan">
        View Interactive Floorplan <MapPin size={12} />
      </Link>
    </Card>
  );
}

/**
 * Mini floorplan preview for the supplier dashboard. Draws a compact
 * pure-SVG version of the 16-station perimeter layout and pulses a
 * champagne marker on the supplier's assigned station.
 * Kept in sync with /venue's canonical layout — uniform tile size,
 * numbers ascend clockwise (N: 01-05, E: 06-10, W: 16, 15, 14, 13, 12, 11 top→bottom).
 */
const MINI = { W: 1200, H: 920,
                ROOM: { x: 90, y: 90, w: 1020, h: 740 } };
const MINI_STA = { w: 74, h: 74 };
const MINI_CORNER = 100;
const MINI_WEST_NUMBERS = [16, 15, 14, 13, 12, 11];

function _miniCoords() {
  const { x, y, w, h } = MINI.ROOM;
  const spaced = (a, b, n) => Array.from({ length: n }, (_, i) => a + ((b - a) / (n + 1)) * (i + 1));
  const MINI_WEST_CORNER = 60;
  const northCX = spaced(x + MINI_CORNER, x + w - MINI_CORNER, 5);
  const eastCY  = spaced(y + MINI_CORNER, y + h - MINI_CORNER, 5);
  const westCY  = spaced(y + MINI_WEST_CORNER, y + h - MINI_WEST_CORNER, 6);
  const northY = y + MINI_STA.h / 2 + 14;
  const eastX  = x + w - MINI_STA.w / 2 - 14;
  const westX  = x + MINI_STA.w / 2 + 14;
  const out = {};
  northCX.forEach((cx, i) => { out[i + 1]  = { x: cx, y: northY }; });
  eastCY.forEach ((cy, i) => { out[6 + i]  = { x: eastX, y: cy }; });
  // West wall: S-16 at top, then S-15..S-11 (clockwise numbering).
  westCY.forEach ((cy, i) => { out[MINI_WEST_NUMBERS[i]] = { x: westX, y: cy }; });
  return out;
}
const DASH_STATION_CENTERS = _miniCoords();

function StationThumbnail({ number }) {
  const center = DASH_STATION_CENTERS[Number(number)];
  return (
    <div className="mt-5 border border-zinc-200 bg-white overflow-hidden" data-testid="dash-station-thumb">
      <div className="relative w-full" style={{ aspectRatio: `${MINI.W} / ${MINI.H}` }}>
        <svg viewBox={`0 0 ${MINI.W} ${MINI.H}`}
             className="absolute inset-0 w-full h-full"
             preserveAspectRatio="xMidYMid meet"
             role="img" aria-label={`Supplier Showcase floor plan · Station S-${String(number).padStart(2, "0")} highlighted`}>
          {/* Room */}
          <rect x={MINI.ROOM.x} y={MINI.ROOM.y} width={MINI.ROOM.w} height={MINI.ROOM.h}
                fill="#FAFAF7" stroke="#1F1F1F" strokeWidth="2" />
          {/* Networking tables */}
          {[
            [0.32, 0.38], [0.5, 0.38], [0.68, 0.38],
            [0.32, 0.60], [0.5, 0.60], [0.68, 0.60],
          ].map(([fx, fy], i) => (
            <circle key={i}
                    cx={MINI.ROOM.x + MINI.ROOM.w * fx}
                    cy={MINI.ROOM.y + MINI.ROOM.h * fy}
                    r="28" fill="#EEEAE1" stroke="#A39171" strokeWidth="1.5" strokeDasharray="3 3" />
          ))}
          {/* Stations */}
          {Object.entries(DASH_STATION_CENTERS).map(([n, c]) => {
            const num = Number(n);
            const isMine = num === Number(number);
            return (
              <g key={n}>
                <rect x={c.x - MINI_STA.w / 2} y={c.y - MINI_STA.h / 2}
                      width={MINI_STA.w} height={MINI_STA.h} rx="4"
                      fill={isMine ? "#A39171" : "#F5F1E9"}
                      stroke={isMine ? "#000" : "#C7BFA9"}
                      strokeWidth={isMine ? 3 : 1.5}
                      strokeDasharray={isMine ? "0" : "4 3"} />
                <text x={c.x} y={c.y + 4} fontSize="12" textAnchor="middle" fontWeight="700"
                      fill={isMine ? "#fff" : "#8A8577"}>{`S-${String(num).padStart(2, "0")}`}</text>
              </g>
            );
          })}
        </svg>
        {/* Pulsing halo on the assigned station */}
        {center && (
          <div className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
               style={{ left: `${(center.x / MINI.W) * 100}%`, top: `${(center.y / MINI.H) * 100}%` }}
               aria-hidden="true">
            <div className="w-16 h-16 rounded-full bg-[#A39171]/25 animate-ping" />
          </div>
        )}
      </div>
    </div>
  );
}

/* -------- 5) Summit Schedule -------- */
function ScheduleCard({ sessions }) {
  const preview = (sessions || []).slice(0, 6);
  const downloadICS = () => {
    const url = `${BACKEND_URL}/api/event.ics`;
    const a = document.createElement("a"); a.href = url; a.download = "summit.ics"; a.click();
  };
  return (
    <Card testId="dash-schedule">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">Summit Schedule</div>
          <div className="serif text-2xl mt-1">Program overview</div>
        </div>
        <Calendar size={16} className="text-[#A39171]" strokeWidth={1.4} />
      </div>
      {preview.length === 0 ? (
        <div className="text-sm text-zinc-500">Schedule coming soon.</div>
      ) : (
        <ul className="grid gap-3">
          {preview.map((s) => (
            <li key={s.id} className="flex items-baseline gap-4 border-b border-zinc-100 pb-3 last:border-0 last:pb-0" data-testid={`dash-session-${s.id}`}>
              <span className="text-xs uppercase tracking-[0.18em] text-zinc-500 shrink-0 tabular-nums w-20">
                {formatTime(s.start)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-black truncate">{s.title}</div>
                {s.location && <div className="text-xs text-zinc-500 mt-0.5">{s.location}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-5 flex gap-2">
        <Link to="/schedule" className="btn-outline-black h-10 px-4 text-xs flex-1 justify-center" data-testid="dash-view-schedule">
          View Schedule <ArrowRight size={12} />
        </Link>
        <button onClick={downloadICS} className="btn-outline-black h-10 px-4 text-xs flex-1 justify-center" data-testid="dash-download-ics">
          <Download size={12} /> Download Calendar
        </button>
      </div>
    </Card>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
}

/* -------- 6) Business Matchmaking -------- */
function MatchmakingCard({ appointments, itinerary }) {
  const slots = itinerary?.time_slots || itinerary?.rounds || [];
  const grid = itinerary?.appointments || [];
  const apptBySlot = Object.fromEntries(grid.map((a) => [a.round_index, a]));
  const hasAnyAppt = grid.length > 0;
  const empty = !hasAnyAppt && appointments.length === 0;
  return (
    <Card testId="dash-matchmaking">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">Business Matchmaking</div>
          <div className="serif text-2xl mt-1">My Business Matchmaking Schedule</div>
        </div>
      </div>
      {empty ? (
        <div className="border border-dashed border-zinc-200 p-6 text-center" data-testid="dash-matchmaking-empty">
          <div className="text-sm text-zinc-500">Business Matchmaking Schedule</div>
          <div className="serif text-xl text-zinc-400 mt-2">No formal matchmaking meetings are currently scheduled for you.</div>
          <p className="text-xs text-zinc-500 mt-3 max-w-sm mx-auto">
            You are invited to visit the Supplier Showcase and participate in open networking throughout the event.
          </p>
        </div>
      ) : (
        <>
          {hasAnyAppt && slots.length > 0 ? (
            <ul className="grid gap-3" data-testid="dash-mm-schedule">
              {slots.map((slot) => {
                const a = apptBySlot[slot.index];
                const isOpen = slot.slot_type === "open";
                if (isOpen) {
                  return (
                    <li key={slot.index}
                        className="flex items-baseline justify-between border-b border-zinc-100 pb-3 bg-[#FBF8F1]/50 px-3 -mx-3"
                        data-testid={`dash-mm-open-${slot.index}`}>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 tabular-nums">{slot.label}</div>
                        <div className="text-sm mt-1 font-medium text-[#7A6A4A]">Open Collaboration &amp; Networking</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Available for informal conversations · SBE-to-SBE · executive introductions</div>
                      </div>
                      {a && (
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#A39171]">Overflow</span>
                      )}
                    </li>
                  );
                }
                if (!a) return null;
                return (
                  <li key={slot.index}
                      className="flex items-baseline justify-between border-b border-zinc-100 pb-3"
                      data-testid={`dash-mm-${a.id}`}>
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 tabular-nums">{slot.label}</div>
                      <div className="text-sm mt-1 font-medium">
                        {itinerary?.role === "prime"
                          ? (a.sbe?.company || a.sbe?.name || a.sbe_company || a.sbe_name || "Open — no SBE assigned")
                          : (a.prime_supplier?.company_name || "Prime Supplier")}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">Table M-{a.table_number}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#A39171]">Matchmaking</span>
                  </li>
                );
              })}
              {appointments.map((a) => (
                <li key={a.id} className="flex items-baseline justify-between border-b border-zinc-100 pb-3 last:border-0 last:pb-0" data-testid={`dash-appt-${a.id}`}>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 tabular-nums">{formatTime(a.start_iso)}</div>
                    <div className="text-sm mt-1 font-medium">{a.tl_leader_name || "T+L Leader"}</div>
                    {a.location && <div className="text-xs text-zinc-500 mt-0.5">{a.location}</div>}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#A39171]">{a.status || "confirmed"}</span>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="grid gap-3">
              {appointments.map((a) => (
                <li key={a.id} className="flex items-baseline justify-between border-b border-zinc-100 pb-3 last:border-0 last:pb-0" data-testid={`dash-appt-${a.id}`}>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 tabular-nums">{formatTime(a.start_iso)}</div>
                    <div className="text-sm mt-1 font-medium">{a.tl_leader_name || "T+L Leader"}</div>
                    {a.location && <div className="text-xs text-zinc-500 mt-0.5">{a.location}</div>}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#A39171]">{a.status || "confirmed"}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-zinc-500 mt-4" data-testid="dash-mm-checkin-note">
            Keep this schedule available when you arrive. Check in at the Business Matchmaking
            Check-In Table inside the Supplier Showcase at least five minutes before your meeting.
          </p>
        </>
      )}
    </Card>
  );
}

/* -------- Representatives (Primary + optional Additional) -------- */
function RepresentativesCard({ exhibitor, setExhibitor }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", title: "",
    email: "", phone: "",
  });
  const rep = exhibitor.additional_representative;
  const canManage = exhibitor._viewer_is_primary !== false;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.title || !form.email) {
      toast.error("First name, last name, title, and email are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/exhibitors/me/additional-representative", form);
      setExhibitor(data.exhibitor);
      toast.success("Additional Representative registered. Welcome email sent.");
      setAdding(false);
      setForm({ first_name: "", last_name: "", title: "", email: "", phone: "" });
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Could not add representative.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${rep.first_name} ${rep.last_name} as Additional Representative? They will lose dashboard access.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/exhibitors/me/additional-representative");
      setExhibitor(data.exhibitor);
      toast.success("Additional Representative removed.");
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Could not remove.");
    } finally { setBusy(false); }
  };

  return (
    <Card testId="dash-representatives">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
          <Users size={18} className="text-zinc-700" strokeWidth={1.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline">Attendees</div>
          <div className="serif text-xl mt-1">Company Representatives</div>
          <div className="text-xs text-zinc-500 mt-1">
            Each Prime Supplier may register up to two attendees.
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-2">
        {/* Primary — always present */}
        <div className="border border-zinc-200 p-3" data-testid="dash-rep-primary">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Primary Representative</div>
          <div className="text-sm font-medium mt-1">{exhibitor.primary_contact || "—"}</div>
          {exhibitor.contact_title && <div className="text-xs text-zinc-500">{exhibitor.contact_title}</div>}
          {exhibitor.email && <div className="text-xs text-zinc-600 mt-1 truncate">{exhibitor.email}</div>}
        </div>

        {/* Additional — only if registered */}
        {rep ? (
          <div className="border border-zinc-200 p-3" data-testid="dash-rep-additional">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Additional Representative</div>
                <div className="text-sm font-medium mt-1 truncate">{rep.first_name} {rep.last_name}</div>
                {rep.title && <div className="text-xs text-zinc-500">{rep.title}</div>}
                <div className="text-xs text-zinc-600 mt-1 truncate">{rep.email}</div>
                {rep.phone && <div className="text-xs text-zinc-500 mt-0.5">{rep.phone}</div>}
              </div>
              {canManage && (
                <button onClick={remove} disabled={busy}
                        className="text-zinc-400 hover:text-red-600 shrink-0"
                        data-testid="dash-rep-remove"
                        aria-label="Remove additional representative">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ) : canManage && !adding ? (
          <button onClick={() => setAdding(true)} disabled={busy}
                  className="border border-dashed border-zinc-300 hover:border-black hover:bg-zinc-50 p-3 text-left transition-colors"
                  data-testid="dash-rep-add">
            <div className="flex items-center gap-2 text-sm">
              <UserPlus size={14} className="text-[#A39171]" />
              <span className="uppercase tracking-[0.18em] text-xs text-zinc-700">Add Representative</span>
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Register your second attendee (name, badge, catering, matchmaking).
            </div>
          </button>
        ) : null}

        {/* Inline add form */}
        {adding && canManage && (
          <form onSubmit={submit} className="border border-zinc-300 p-4 grid gap-3" data-testid="dash-rep-form">
            <div className="flex items-center justify-between">
              <div className="overline">Add Representative</div>
              <button type="button" onClick={() => setAdding(false)}
                      className="text-zinc-400 hover:text-black"
                      aria-label="Cancel">
                <X size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input required placeholder="First name *"
                     value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                     data-testid="dash-rep-first-name"
                     className="h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-black" />
              <input required placeholder="Last name *"
                     value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                     data-testid="dash-rep-last-name"
                     className="h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-black" />
            </div>
            <input required placeholder="Title *"
                   value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   data-testid="dash-rep-title"
                   className="h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-black" />
            <input required type="email" placeholder="Email *"
                   value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   data-testid="dash-rep-email"
                   className="h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-black" />
            <input placeholder="Mobile phone (optional)"
                   value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   data-testid="dash-rep-phone"
                   className="h-10 px-3 border border-zinc-300 text-sm focus:outline-none focus:border-black" />
            <button disabled={busy}
                    className="btn-black h-11 justify-center disabled:opacity-40"
                    data-testid="dash-rep-submit">
              {busy ? "Adding…" : "Register Representative"}
            </button>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 text-center">
              A welcome email with dashboard access will be sent immediately.
            </div>
          </form>
        )}

        {!canManage && !rep && (
          <div className="text-xs text-zinc-500 italic border border-zinc-200 border-dashed p-3">
            No Additional Representative yet.
          </div>
        )}
      </div>
    </Card>
  );
}


/* -------- 7) Summit Concierge -------- */
function ConciergeCard() {
  return (
    <Card testId="dash-concierge">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-full bg-[#A39171]/10 border border-[#A39171]/40 flex items-center justify-center shrink-0">
          <Sparkles size={18} className="text-[#A39171]" strokeWidth={1.4} />
        </div>
        <div className="flex-1">
          <div className="overline">Summit Concierge</div>
          <div className="serif text-xl mt-1">Your executive assistant.</div>
          <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
            Ask anything about your registration, station, schedule, parking, or logistics.
          </p>
        </div>
      </div>
      <Link to="/ask" className="btn-black mt-4 w-full h-10 text-xs justify-center" data-testid="dash-open-concierge">
        Open Concierge <MessageSquare size={12} />
      </Link>
    </Card>
  );
}

/* -------- 8) Document Center -------- */
function DocumentCenter({ exhibitor, documents, eventDocs }) {
  const receiptUrl = exhibitor?.receipt_url; // set by the payment/webhook if we start capturing it
  const publicDocs = documents.slice(0, 8);
  const launchDocs = (eventDocs || []).slice(0, 5);

  return (
    <Card testId="dash-documents">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">Event Documents</div>
          <div className="serif text-2xl mt-1">Everything you need</div>
        </div>
        <FileText size={16} className="text-[#A39171]" strokeWidth={1.4} />
      </div>

      <div className="grid gap-2">
        {/* Launch documents (structured, PDF-exportable) */}
        {launchDocs.map((d) => (
          <Link key={d.slug} to={`/documents/${d.slug}`}
                className="flex items-center gap-3 py-2.5 border-b border-zinc-100 last:border-0 hover:bg-zinc-50 -mx-2 px-2 transition-colors"
                data-testid={`dash-doc-launch-${d.slug}`}>
            <FileText size={14} className="text-[#A39171]" strokeWidth={1.4} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-black truncate">{d.title}</div>
              {d.subtitle && <div className="text-[10px] text-zinc-500 truncate mt-0.5">{d.subtitle}</div>}
            </div>
            <ChevronRight size={12} className="text-zinc-400 shrink-0" />
          </Link>
        ))}

        {launchDocs.length > 0 && (
          <Link to="/documents" className="text-[11px] uppercase tracking-[0.22em] text-[#A39171] hover:text-black inline-flex items-center gap-1 pt-3" data-testid="dash-doc-viewall">
            View all documents <ChevronRight size={12} />
          </Link>
        )}

        {/* Personal documents */}
        {receiptUrl && (
          <DocRow icon={CreditCard} label="Payment Receipt" href={receiptUrl} testId="dash-doc-receipt" />
        )}
        <DocRow icon={CheckCircle2} label="Registration Confirmation"
          note="Sent via email · check your inbox from info@displayamerica.com"
          testId="dash-doc-confirmation" muted />

        {/* Uploaded event-wide files (agenda PDF, wifi, etc.) */}
        {publicDocs.map((d) => (
          <DocRow key={d.id} icon={FileText}
            label={humanKind(d.kind)} sub={d.filename}
            href={`${BACKEND_URL}/api/files/${d.file_id}`} testId={`dash-doc-${d.id}`} />
        ))}
      </div>
    </Card>
  );
}

function humanKind(k) {
  return ({
    agenda: "Event Agenda",
    venue_map: "Venue Map",
    parking: "Parking Instructions",
    wifi: "Wi-Fi Information",
    emergency_contacts: "Emergency Contacts",
    supplier_guide: "Supplier Guide",
  })[k] || k;
}

function DocRow({ icon: Icon, label, sub, note, href, muted, testId }) {
  const inner = (
    <>
      <Icon size={14} className={muted ? "text-zinc-400" : "text-[#A39171]"} strokeWidth={1.4} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${muted ? "text-zinc-500" : "text-black"} truncate`}>{label}</div>
        {sub && <div className="text-[10px] text-zinc-500 truncate mt-0.5">{sub}</div>}
        {note && <div className="text-[10px] text-zinc-500 truncate mt-0.5">{note}</div>}
      </div>
      {href && <ChevronRight size={12} className="text-zinc-400 shrink-0" />}
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer"
        className="flex items-center gap-3 border-b border-zinc-100 py-2.5 last:border-0 hover:bg-zinc-50 -mx-1 px-1"
        data-testid={testId}>
        {inner}
      </a>
    );
  }
  return (
    <div className="flex items-center gap-3 border-b border-zinc-100 py-2.5 last:border-0" data-testid={testId}>
      {inner}
    </div>
  );
}

/* -------- 9) Notifications -------- */
function NotificationsCard({ updates, exhibitor, production, proof }) {
  // Merge admin-driven updates with derived milestone events, newest first.
  const derived = [];
  if (exhibitor.created_at) derived.push({ id: "reg", title: "Registration completed.", ts: exhibitor.created_at });
  if (["payment_received", "complete", "paid"].includes(exhibitor.payment_status || "") && exhibitor.confirmation_email_sent_at) {
    derived.push({ id: "pay", title: "Station assignment confirmed.", ts: exhibitor.confirmation_email_sent_at });
  }
  if (exhibitor.logo_file_id) derived.push({ id: "logo", title: "Logo received.", ts: exhibitor.updated_at });
  // Multi-proof derived notifications — one entry per proof, so suppliers can
  // tell "Graphics ready" apart from "Color rendering approved".
  (proof?.proofs || []).forEach((p) => {
    const label = p.title || "Production proof";
    if (p.status === "ready" && p.ready_at) {
      derived.push({ id: `proof_ready_${p.id}`, title: `${label} ready. Please review.`, ts: p.ready_at, priority: "high" });
    } else if (p.status === "approved" && p.approved_at) {
      derived.push({ id: `proof_appr_${p.id}`, title: `${label} approved.`, ts: p.approved_at });
    } else if (p.status === "revision_requested" && p.revision_at) {
      derived.push({ id: `proof_rev_${p.id}`, title: `${label} — revision requested. Awaiting new proof.`, ts: p.revision_at });
    }
  });
  const currentStage = production?.stages?.find((s) => s.current)?.label;
  if (currentStage && !["Registration Complete", "Payment Received"].includes(currentStage)) {
    derived.push({ id: "stage", title: `Production update · ${currentStage}.`, ts: exhibitor.updated_at });
  }

  const all = [
    ...updates.map((u) => ({ id: `u_${u.id}`, title: u.title, body: u.body, ts: u.created_at, priority: u.priority })),
    ...derived,
  ].filter((x) => x.title);

  all.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));

  return (
    <Card testId="dash-notifications">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">Notifications</div>
          <div className="serif text-2xl mt-1">Recent updates</div>
        </div>
        <Bell size={16} className="text-[#A39171]" strokeWidth={1.4} />
      </div>
      {all.length === 0 ? (
        <div className="text-sm text-zinc-500">No updates yet.</div>
      ) : (
        <ul className="grid gap-3">
          {all.slice(0, 6).map((n) => (
            <li key={n.id} className="flex items-start gap-3 border-b border-zinc-100 pb-3 last:border-0 last:pb-0" data-testid={`dash-notif-${n.id}`}>
              <div className={`h-1.5 w-1.5 rounded-full mt-2 shrink-0 ${n.priority === "high" ? "bg-red-500" : "bg-[#A39171]"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm">{n.title}</div>
                {n.body && <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{n.body}</div>}
                {n.ts && <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 mt-1">{fmtDate(n.ts)}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return `Today · ${formatTime(iso)}`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}

/* -------- Proof Review (multi-proof) -------- */
function ProofReviewCard({ proof, setProof, setProduction }) {
  const proofs = proof?.proofs || [];
  if (proofs.length === 0) return null;

  const rollup = proof?.proof_status; // ready | approved | revision_requested

  const refresh = async () => {
    const [{ data: p }, { data: prod }] = await Promise.all([
      api.get("/exhibitors/me/proof"), api.get("/exhibitors/me/production"),
    ]);
    setProof(p.proof); setProduction(prod);
  };

  const cardBorder = rollup === "approved" ? "border-emerald-500"
    : rollup === "revision_requested" ? "border-amber-500"
    : "border-[#A39171]";

  const headline = rollup === "approved" ? "All proofs approved."
    : rollup === "revision_requested" ? "Revisions requested."
    : proofs.length === 1 ? "Your production proof is ready."
    : `${proofs.filter((p) => p.status === "ready").length} of ${proofs.length} proofs awaiting your review.`;

  return (
    <Card testId="dash-proof" className={`border-l-4 ${cardBorder}`}>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="overline">Proof Review</div>
          <div className="serif text-2xl mt-1">{headline}</div>
          {proofs.length > 1 && (
            <div className="text-xs text-zinc-500 mt-1">
              Each proof is reviewed independently. Production begins once every proof is approved.
            </div>
          )}
        </div>
        {rollup === "approved" && <Lock size={16} className="text-emerald-700" strokeWidth={1.4} />}
      </div>

      <div className="mt-5 grid gap-5">
        {proofs.map((entry) => (
          <ProofEntryBlock key={entry.id} entry={entry} refresh={refresh} />
        ))}
      </div>
    </Card>
  );
}

function proofTypeLabel(t) {
  const s = (t || "").toLowerCase();
  if (s === "graphics") return "Graphics";
  if (s === "rendering" || s === "color_rendering") return "Color Rendering";
  if (s === "signage") return "Signage";
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Proof";
}

function ProofEntryBlock({ entry, refresh }) {
  const [busy, setBusy] = useState(false);
  const [revising, setRevising] = useState(false);
  const [revisionComment, setRevisionComment] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [note, setNote] = useState("");

  const status = entry.status; // ready | approved | revision_requested
  const fileHref = entry.file_id ? `${BACKEND_URL}/api/files/${entry.file_id}` : null;

  const approve = async () => {
    if (!window.confirm(`Approve "${entry.title}"? Once approved this proof is locked.`)) return;
    setBusy(true);
    try {
      await api.post(`/exhibitors/me/proofs/${entry.id}/approve`);
      toast.success(`${entry.title} approved.`);
      await refresh();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Approval failed.");
    } finally { setBusy(false); }
  };

  const submitRevision = async () => {
    if (revisionComment.trim().length < 5) {
      toast.error("Please describe the changes you need (a few words is enough).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/exhibitors/me/proofs/${entry.id}/revise`, { comment: revisionComment });
      toast.success("Revision request sent to the design team.");
      setRevising(false); setRevisionComment("");
      await refresh();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not send request.");
    } finally { setBusy(false); }
  };

  const submitComment = async () => {
    if (note.trim().length < 1) return;
    setBusy(true);
    try {
      await api.post(`/exhibitors/me/proofs/${entry.id}/comment`, { comment: note });
      toast.success("Comment sent to the design team.");
      setNote(""); setCommenting(false);
      await refresh();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not send comment.");
    } finally { setBusy(false); }
  };

  const statusBadge = status === "approved"
    ? <span className="text-[10px] uppercase tracking-[0.18em] border border-emerald-500 text-emerald-700 bg-emerald-50 px-2 py-1">Approved · Locked</span>
    : status === "revision_requested"
    ? <span className="text-[10px] uppercase tracking-[0.18em] border border-amber-500 text-amber-700 bg-amber-50 px-2 py-1">Revision requested</span>
    : <span className="text-[10px] uppercase tracking-[0.18em] border border-[#A39171] text-[#A39171] bg-[#A39171]/10 px-2 py-1">Awaiting your review</span>;

  return (
    <div className="border border-zinc-200 p-4" data-testid={`dash-proof-entry-${entry.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{proofTypeLabel(entry.proof_type)}</div>
          <div className="text-base font-medium mt-1 truncate">{entry.title}</div>
        </div>
        {statusBadge}
      </div>

      {fileHref && (
        <a href={fileHref} target="_blank" rel="noreferrer"
          className="mt-3 flex items-center gap-3 border border-zinc-200 p-3 hover:border-black transition-colors"
          data-testid={`dash-proof-file-${entry.id}`}>
          <FileText size={18} className="text-[#A39171] shrink-0" strokeWidth={1.4} />
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{entry.display_label || entry.filename || "Production proof"}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Open · New tab</div>
          </div>
          <Eye size={14} className="text-zinc-400 shrink-0" />
        </a>
      )}

      {entry.admin_notes && (
        <div className="mt-3 border-l-2 border-zinc-200 pl-4 text-xs text-zinc-600" data-testid={`dash-proof-notes-${entry.id}`}>
          <div className="overline mb-1">Design notes</div>
          {entry.admin_notes}
        </div>
      )}

      {status === "ready" && !revising && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2" data-testid={`dash-proof-actions-${entry.id}`}>
          <button onClick={approve} disabled={busy}
            className="btn-black h-10 px-4 flex-1 justify-center" data-testid={`dash-proof-approve-${entry.id}`}>
            <CheckCircle2 size={14} /> {busy ? "…" : "Approve"}
          </button>
          <button onClick={() => setRevising(true)} disabled={busy}
            className="btn-outline-black h-10 px-4 flex-1 justify-center" data-testid={`dash-proof-revise-${entry.id}`}>
            <RotateCcw size={14} /> Request Revision
          </button>
        </div>
      )}

      {status === "ready" && revising && (
        <div className="mt-4 border-t border-zinc-100 pt-4" data-testid={`dash-proof-revision-form-${entry.id}`}>
          <label className="overline">What needs to change?</label>
          <textarea rows={4} value={revisionComment} onChange={(e) => setRevisionComment(e.target.value)}
            placeholder="Short, specific — the design team will act on it directly."
            data-testid={`dash-proof-revision-comment-${entry.id}`}
            className="w-full mt-2 rounded-none border border-zinc-300 p-3 text-sm focus:outline-none focus:border-black" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500">{revisionComment.trim().length}/4000</div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button onClick={submitRevision} disabled={busy || revisionComment.trim().length < 5}
              className="btn-black h-10 px-4 flex-1 justify-center disabled:opacity-40"
              data-testid={`dash-proof-revision-submit-${entry.id}`}>
              {busy ? "…" : "Send Revision Request"}
            </button>
            <button onClick={() => { setRevising(false); setRevisionComment(""); }}
              className="h-10 px-4 border border-zinc-300 text-xs uppercase tracking-[0.18em]"
              data-testid={`dash-proof-revision-cancel-${entry.id}`}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {status === "approved" && (
        <div className="mt-3 text-xs text-zinc-500" data-testid={`dash-proof-approved-note-${entry.id}`}>
          Locked and forwarded to production on {fmtDateTime(entry.approved_at)}. Thank you.
        </div>
      )}

      {status === "revision_requested" && (
        <div className="mt-3" data-testid={`dash-proof-revision-status-${entry.id}`}>
          <div className="text-xs text-zinc-500">
            Requested on {fmtDateTime(entry.revision_at)}. The design team will send a revised proof shortly.
          </div>
          {entry.revision_comment && (
            <div className="mt-2 border-l-2 border-amber-500 pl-4 text-xs text-zinc-700">
              <div className="overline mb-1">Your comment</div>
              {entry.revision_comment}
            </div>
          )}
        </div>
      )}

      {/* Supplier comment thread — visible at every status, additive feedback */}
      <div className="mt-4 border-t border-zinc-100 pt-3" data-testid={`dash-proof-thread-${entry.id}`}>
        <div className="flex items-center justify-between">
          <div className="overline">Comments ({(entry.supplier_notes || []).length})</div>
          {!commenting && status !== "approved" && (
            <button onClick={() => setCommenting(true)}
              className="text-[10px] uppercase tracking-[0.18em] underline underline-offset-4 text-zinc-500 hover:text-black"
              data-testid={`dash-proof-add-comment-${entry.id}`}>
              <MessageSquare size={10} className="inline mr-1" /> Add comment
            </button>
          )}
        </div>
        {(entry.supplier_notes || []).length > 0 && (
          <ul className="mt-2 grid gap-2">
            {(entry.supplier_notes || []).map((n) => (
              <li key={n.id} className="text-xs text-zinc-700 border-l-2 border-[#A39171] pl-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  {n.author || "You"} · {fmtDateTime(n.at)}
                </div>
                <div className="mt-1 whitespace-pre-wrap">{n.comment}</div>
              </li>
            ))}
          </ul>
        )}
        {commenting && (
          <div className="mt-2">
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note for the design team (question, thought, or clarification)…"
              data-testid={`dash-proof-comment-input-${entry.id}`}
              className="w-full rounded-none border border-zinc-300 p-3 text-sm focus:outline-none focus:border-black" />
            <div className="mt-2 flex gap-2">
              <button onClick={submitComment} disabled={busy || note.trim().length < 1}
                className="btn-black h-9 px-4 disabled:opacity-40"
                data-testid={`dash-proof-comment-submit-${entry.id}`}>
                {busy ? "…" : "Send"}
              </button>
              <button onClick={() => { setCommenting(false); setNote(""); }}
                className="h-9 px-3 border border-zinc-300 text-xs uppercase tracking-[0.18em]"
                data-testid={`dash-proof-comment-cancel-${entry.id}`}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

/* -------- Card shell -------- */
function Card({ children, testId, className = "" }) {
  return (
    <div className={`bg-white border border-zinc-200 p-6 md:p-7 ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}
